import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { MONO } from "@/components/home-a/theme";
import { Glyph, ScoreBadge } from "@/components/home-a/Parts";

/**
 * Exit-intent offer: a free account with 30 credits.
 *
 * The credits come from the signup trigger (grant_signup_credits: 30 credits,
 * valid for 30 days), so the numbers here must follow that trigger. A CRO
 * analysis costs 15 credits (CREDITS_PER_PAGE.cro_v3), which is where the
 * "two analyses" comes from.
 */
export const SIGNUP_CREDITS = 30;
const CREDITS_VALID_DAYS = 30;

export function SignupCreditsPopup({ onClose }: { onClose: () => void }) {
  const { language } = useLanguage();
  const el = language === "el";
  const tr = (g: string, e: string) => (el ? g : e);

  const points = [
    tr("2 πλήρεις CRO αναλύσεις, σε desktop και mobile", "2 full CRO audits, on desktop and mobile"),
    tr("Attention heatmap και τα 3 πρώτα πράγματα που πρέπει να διορθώσετε", "An attention heatmap and the 3 things to fix first"),
    tr("Χωρίς κάρτα. Τα credits ισχύουν 30 ημέρες.", `No card needed. Credits last ${CREDITS_VALID_DAYS} days.`),
  ];

  return (
    <div data-section="exit_popup" className="relative grid grid-cols-1 sm:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
      <button
        type="button"
        onClick={onClose}
        aria-label={tr("Κλείσιμο", "Close")}
        className="absolute right-3 top-3 z-10 grid h-9 w-9 place-items-center rounded-full text-[hsl(var(--ec-muted))] transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M2 2l10 10M12 2L2 12" /></svg>
      </button>

      {/* the offer, drawn as the thing you get */}
      <div className="relative hidden flex-col items-center justify-center overflow-hidden border-r border-border px-6 py-10 sm:flex">
        <div aria-hidden className="pointer-events-none absolute -left-20 -top-20 h-72 w-72 rounded-full bg-[radial-gradient(circle,hsl(var(--primary)/0.16),transparent_70%)]" />
        <div className="relative text-center">
          <p className="text-[76px] font-semibold leading-none tracking-[-0.05em] text-foreground tabular-nums">{SIGNUP_CREDITS}</p>
          <p className="mt-1 text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--ec-mint))]" style={{ fontFamily: MONO }}>
            {tr("δωρεάν credits", "free credits")}
          </p>
        </div>
        <div className="relative mt-7 flex items-center gap-3 rounded-[16px] border border-[hsl(var(--ec-line-strong))] bg-background/60 px-4 py-3">
          <ScoreBadge size={64} score={58} caption="" />
          <div className="text-left">
            <p className="text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--ec-muted))]" style={{ fontFamily: MONO }}>{tr("το report σας", "your report")}</p>
            <p className="mt-0.5 text-[13.5px] font-medium text-foreground">{tr("Score, ευρήματα, λύσεις", "Score, findings, fixes")}</p>
          </div>
        </div>
      </div>

      <div className="px-6 py-8 sm:px-8 sm:py-10">
        <p className="inline-flex items-center gap-2.5 text-[12px] uppercase tracking-[0.12em] text-[hsl(var(--ec-mint))]" style={{ fontFamily: MONO }}>
          <span aria-hidden className="h-px w-[18px] bg-[hsl(var(--ec-mint))]" />
          {tr("Δωρεάν λογαριασμός", "Free account")}
        </p>
        <h2 className="mt-3 pr-8 text-[26px] font-semibold leading-[1.12] tracking-[-0.03em] text-foreground sm:text-[28px]">
          {tr(`Κάντε δωρεάν εγγραφή και πάρτε ${SIGNUP_CREDITS} credits τώρα`, `Sign up free and get ${SIGNUP_CREDITS} credits now`)}
        </h2>
        <p className="mt-3 text-[15px] leading-relaxed text-[hsl(var(--ec-text-2))]">
          {tr("Φτάνουν για να δείτε τι κρατά πίσω τις πωλήσεις στις δύο πιο σημαντικές σελίδες σας.", "Enough to see what holds back sales on your two most important pages.")}
        </p>

        <ul className="mt-5 space-y-2.5">
          {points.map((p) => (
            <li key={p} className="flex items-start gap-2.5 text-[14.5px] leading-snug text-foreground">
              <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border border-[hsl(var(--ec-mint)/0.5)] text-[hsl(var(--ec-mint))]">
                <Glyph name="check" size={11} />
              </span>
              {p}
            </li>
          ))}
        </ul>

        <Link
          to="/signup"
          onClick={onClose}
          data-track="exit_popup_signup"
          className="ec-cta mt-7 inline-flex h-12 w-full items-center justify-center gap-2 whitespace-nowrap rounded-[10px] px-5 text-[15px] font-semibold"
        >
          {tr("Δημιουργία δωρεάν λογαριασμού", "Create my free account")}
        </Link>
        <div className="mt-3 flex items-center justify-between gap-3 text-[13px]">
          <Link to="/login" onClick={onClose} className="text-[hsl(var(--ec-text-2))] transition-colors hover:text-foreground">
            {tr("Έχετε λογαριασμό; Σύνδεση", "Have an account? Log in")}
          </Link>
          <button type="button" onClick={onClose} className="text-[hsl(var(--ec-muted))] transition-colors hover:text-foreground">
            {tr("Όχι τώρα", "Not now")}
          </button>
        </div>
      </div>
    </div>
  );
}
